<template>
    <div v-if="visible" class="governance-warning" :class="`governance-warning--${severity}`" role="status">
        <v-icon :color="severity === 'block' ? '#7f1d1d' : '#92400e'" class="mr-2">
            {{ severity === 'block' ? 'mdi-lock' : 'mdi-alert-octagon' }}
        </v-icon>
        <div class="governance-warning__body">
            <span class="governance-warning__title">
                <strong>{{ bannerTitle }}</strong>
                <span class="governance-warning__subtitle">
                    · {{ bannerSubtitle }}
                </span>
            </span>
            <div class="governance-warning__rooms">
                <v-chip
                    v-for="room in displayedRooms"
                    :key="room.id"
                    small label
                    :color="severity === 'block' ? '#fecaca' : '#fde68a'"
                    :text-color="severity === 'block' ? '#7f1d1d' : '#78350f'"
                    class="mr-1 governance-warning__chip"
                    @click="goToRoom(room)"
                >
                    <v-icon left small>mdi-lock-outline</v-icon>
                    {{ shortName(room) }}
                    <span v-if="room.percent" class="ml-1">({{ Math.round(room.percent) }}%)</span>
                </v-chip>
                <span v-if="hiddenCount > 0" class="text-caption ml-1">
                    +{{ hiddenCount }}
                </span>
            </div>
        </div>
        <v-btn
            v-if="!isOnVacancyControl || severity === 'warning'"
            small outlined
            :color="severity === 'block' ? '#7f1d1d' : '#92400e'"
            class="ml-2"
            @click="goToVacancyControl"
        >
            <v-icon left small>mdi-arrow-right</v-icon>
            {{ isOnVacancyControl ? 'Ir ao topo' : 'Ver Gestão de Vagas' }}
        </v-btn>
    </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'

const MAX_VISIBLE_CHIPS = 3

export default {
    name: 'GovernanceWarningBanner',

    computed: {
        ...mapGetters('governance', ['isWarningOnly', 'isBlocked', 'blockingRooms', 'totalCriticalSlots']),
        ...mapGetters('auth', ['hasAcesso']),
        visible() {
            // Esconde em pré-requisitos. Aparece tanto em is_blocked (em
            // /vacancy-control onde o overlay deu lugar à tela operacional)
            // quanto em is_warning_only (admin com bypass).
            if (this.isOnPrerequisiteRoute) return false
            return (this.isBlocked || this.isWarningOnly) && this.blockingRooms.length > 0
        },
        isOnPrerequisiteRoute() {
            const name = this.$route?.name
            return ['worktime', 'login', 'availability-teacher'].includes(name)
        },
        severity() {
            return this.isBlocked ? 'block' : 'warning'
        },
        bannerTitle() {
            return this.severity === 'block'
                ? 'Acesso bloqueado às demais funções'
                : 'Bloqueio de governança ativo'
        },
        bannerSubtitle() {
            const salas = this.rooms.length
            const slots = this.totalCriticalSlots || salas
            // Inclui ambos: sala (escopo do bloqueio) e slots (linhas
            // críticas que ela vê na tabela). Antes só mostrava salas e
            // confundia quando 1 sala tinha múltiplos horários em red.
            const detalhe = slots !== salas
                ? `${salas} sala${salas === 1 ? '' : 's'} (${slots} horário${slots === 1 ? '' : 's'})`
                : `${salas} sala${salas === 1 ? '' : 's'}`
            return this.severity === 'block'
                ? `Resolva ${detalhe} em superlotação crítica para liberar a navegação.`
                : `${detalhe} em superlotação crítica reincidente. Operadores estão impedidos até a regularização.`
        },
        rooms() {
            return this.blockingRooms
        },
        displayedRooms() {
            return this.rooms.slice(0, MAX_VISIBLE_CHIPS)
        },
        hiddenCount() {
            return Math.max(0, this.rooms.length - MAX_VISIBLE_CHIPS)
        },
        isOnVacancyControl() {
            return this.$route?.path === '/vacancy-control'
        },
        // Sem período/instituição selecionados, /vacancy-control rebota
        // pra /worktime via beforeEach guard. Mostrar isso no botão evita
        // a sensação de "click sem efeito".
        needsPeriodSelection() {
            return !this.hasAcesso
        },
        ctaLabel() {
            if (this.isOnVacancyControl) return 'Ir ao topo'
            return 'Ver Gestão de Vagas'
        },
    },

    methods: {
        ...mapActions('governance', ['ActionSelectSuggestedContext']),
        async goToVacancyControl() {
            if (this.needsPeriodSelection) {
                const ok = await this.ActionSelectSuggestedContext()
                if (ok) {
                    window.location.assign('/vacancy-control')
                    return
                }
                if (this.$route?.name !== 'worktime') {
                    this.$router.push({ name: 'worktime' }).catch(() => {})
                }
                return
            }
            if (!this.isOnVacancyControl) {
                this.$router.push({ path: '/vacancy-control' }).catch(() => {})
            } else {
                window.scrollTo({ top: 0, behavior: 'smooth' })
            }
        },
        goToRoom(room) {
            if (this.needsPeriodSelection) {
                this.goToVacancyControl()
                return
            }
            const path = `/vacancy-control/ambience/${room.id}`
            if (this.$route?.path !== path) {
                this.$router.push({ path }).catch(() => {})
            }
        },
        shortName(room) {
            const name = room.name || `Sala #${room.id}`
            // Remove prefixos verbosos do slug "Bloco X | SALA: Y | <nome>"
            const parts = String(name).split('|').map(s => s.trim())
            return parts[parts.length - 1] || name
        },
    },
}
</script>

<style scoped>
.governance-warning {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 16px;
    font-size: 13px;
    line-height: 1.4;
    /* No flow normal — fica logo abaixo do header (SysCoreNavigationBar). */
    position: relative;
    z-index: 4;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}
.governance-warning--warning {
    background: #fef3c7;
    border-bottom: 1px solid #fbbf24;
    color: #78350f;
}
.governance-warning--block {
    background: #fee2e2;
    border-bottom: 1px solid #ef4444;
    color: #7f1d1d;
}
.governance-warning__body {
    flex: 1 1 auto;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px 12px;
    min-width: 0;
}
.governance-warning__title {
    white-space: nowrap;
}
.governance-warning__subtitle {
    font-weight: 400;
}
.governance-warning__rooms {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 4px;
}
.governance-warning__chip {
    cursor: pointer;
}
.governance-warning__chip:hover {
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.15);
}
@media (max-width: 768px) {
    .governance-warning__subtitle {
        display: none;
    }
}
</style>
